/**
 * Which door a screen belongs to, on the browser side.
 *
 * The three doors are the same three the rules package names. Kept as a small local
 * file rather than reaching for the whole rules package in the browser, because all
 * the screens need is the address and the wording.
 */
export type SignInDoorName = "organizer" | "attendee" | "presenter" | "school";

export function doorApiBase(door: SignInDoorName): string {
  return `/api/${door}`;
}

/** Where a door's own pages live. The guide is the front door, so it sits at the root. */
export function doorPath(door: SignInDoorName): string {
  return door === "attendee" ? "" : `/${door}`;
}

export function doorHome(door: SignInDoorName): string {
  return doorPath(door) || "/";
}

/**
 * What a refused request said, as one sentence somebody can act on.
 *
 * Every refusal now carries both what happened and what to do next, so the screens
 * show both. The old code showed only a message, and most of those messages said
 * "try again shortly" whatever had actually gone wrong.
 */
export async function refusalText(response: Response): Promise<string> {
  try {
    const body = await response.json() as { message?: string; nextStep?: string };
    const parts = [body.message, body.nextStep].filter((part): part is string => Boolean(part));
    if (parts.length > 0) return parts.join(" ");
  } catch {
    // A response with no body still needs something on screen.
  }
  return `That did not work (${response.status}).`;
}

export interface Refusal {
  message: string;
  nextStep: string | null;
}

/** The same thing, kept in two parts for a screen that lays them out separately. */
export async function readRefusal(response: Response): Promise<Refusal> {
  try {
    const body = await response.json() as { message?: string; nextStep?: string };
    if (body.message) return { message: body.message, nextStep: body.nextStep ?? null };
  } catch {
    // Falls through to the status.
  }
  return { message: `That did not work (${response.status}).`, nextStep: null };
}
